import {
  ITimeOffPolicyData,
  ITimeOffTemplateData,
  ITimeOffTypeData
} from '@/models';
import { ITimeOffExportBalancesParams, ITimeOffService } from './timeoff.types';
import { AxiosInstance } from 'axios';

export class TimeOffService implements ITimeOffService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/timeoffs/';
  }

  fetchTimeOffTypes(): Promise<ITimeOffTypeData[]> {
    return this.axios
      .get<ITimeOffTypeData[]>(`${this.endpoint}`)
      .then(({ data }) => data);
  }

  fetchAvailableTimeOffTypes(): Promise<ITimeOffTypeData[]> {
    return this.axios
      .get<ITimeOffTypeData[]>(`${this.endpoint}`)
      .then(({ data }) => data);
  }

  fetchTimeOffTemplates(): Promise<ITimeOffTemplateData[]> {
    return this.axios
      .get<ITimeOffTemplateData[]>(`${this.endpoint}templates/`)
      .then(({ data }) => data);
  }

  fetchTimeOffTemplate(id: number): Promise<ITimeOffTemplateData> {
    return this.axios
      .get<ITimeOffTemplateData>(`${this.endpoint}templates/${id}`)
      .then(({ data }) => data);
  }

  downloadTimeOffAttachment(id: number, attachmentId: number) {
    return this.axios
      .get<Blob>(
        `${this.endpoint}requests/${id}/download-attachment/${attachmentId}/`,
        {
          responseType: 'blob'
        }
      )
      .then(({ data }) => data);
  }

  deleteTimeOffAttachment(id: number, attachmentId: number) {
    return this.axios
      .delete(
        `${this.endpoint}requests/${id}/remove-attachment/${attachmentId}/`
      )
      .then(({ data }) => data);
  }

  fetchTimeOffPolicies(): Promise<ITimeOffPolicyData[]> {
    return this.axios
      .get<ITimeOffPolicyData[]>(`${this.endpoint}policies/`)
      .then(({ data }) => data);
  }

  createTimeOffPolicy(data: ITimeOffPolicyData): Promise<ITimeOffPolicyData> {
    return this.axios
      .post<ITimeOffPolicyData>(`${this.endpoint}policies/`, data)
      .then(({ data }) => data);
  }

  updateTimeOffPolicy(data: ITimeOffPolicyData): Promise<ITimeOffPolicyData> {
    return this.axios
      .patch<ITimeOffPolicyData>(`${this.endpoint}policies/${data.id}/`, data)
      .then(({ data }) => data);
  }

  deleteTimeOffPolicy(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}policies/${id}/`);
  }

  rollOverEntitlement(): Promise<any> {
    return this.axios.post(
      `${this.endpoint}policies/apply-entitlements/`,
      undefined,
      {
        timeout: 5 * (60 * 1000) //5 minute
      }
    );
  }

  incrementEntitlementYears(year: number): Promise<any> {
    return this.axios
      .put<ITimeOffPolicyData[]>(
        `${this.endpoint}policies/increment-years/`,
        undefined,
        {
          params: {
            year
          }
        }
      )
      .then(({ data }) => data);
  }

  exportTimeOffBalances(params: ITimeOffExportBalancesParams): Promise<Blob> {
    return this.axios
      .get<Blob>(`${this.endpoint}requests/balances/export/`, {
        responseType: 'blob',
        timeout: 1000 * 60 * 5, //5mins
        params: {
          employee_is_active: params?.employee_is_active,
          employee_type: params?.employee_type,
          employee_location: params?.employee_location,
          timeoff__in: params?.timeoff__in,
          year: params?.year,
          start_after: params?.start_after,
          start_before: params?.start_before
        }
      })
      .then(({ data }) => data);
  }
}
